import { useDeviceType } from '~/composables/useDeviceType'

const KNOWN_RATIOS = {
  '32/9': 32 / 9,
  '21/9': 21 / 9,
  '18/9': 18 / 9,
  '16/9': 16 / 9,
  '16/10': 16 / 10,
  '3/2': 3 / 2,
  '4/3': 4 / 3,
  '1/1': 1,
  '9/16': 9 / 16,
  '7/10': 7 / 10,
  '2/1': 2 / 1,
  '3/1': 3 / 1,
}

const THUMB_QUALITY = 60

export function useVimeoVideo(desktopLink: string, mobileLink?: string) {
  const thumbnailUrl = ref<string | null>(null)
  const aspectRatioLabel = ref<string | null>(null)
  const thumbnailResolution = ref<number[]>([])
  const loaded = ref(false)

  const { isMobile } = useDeviceType()
  const url = computed(() =>
    isMobile.value && mobileLink ? mobileLink : desktopLink,
  )

  watch(
    url,
    async () => {
      const { ratio, thumbnailUrl: initalUrl } = await fetchVideoInfo(url.value)
      const { resolution, ratioLabel } = getThumbnailDimensions(ratio)

      thumbnailUrl.value = `${initalUrl.split('-d_')[0]}-d_${resolution}?q=${THUMB_QUALITY}`
      aspectRatioLabel.value = ratioLabel
      loaded.value = true
    },
    { immediate: true },
  )

  return {
    url,
    thumbnailUrl,
    aspectRatioLabel,
    thumbnailResolution,
    loaded,
  }
}

function getThumbnailDimensions(ratio: number) {
  const BASE_RESOLUTION = 1080
  const tolerance = 0.1
  let closestRatio = KNOWN_RATIOS['16/9']
  let closestRatioLabel = '16/9'

  for (const [label, value] of Object.entries(KNOWN_RATIOS)) {
    if (Math.abs(ratio - value) < tolerance) {
      closestRatio = value
      closestRatioLabel = label
    }
  }

  const resolution =
    ratio > 1
      ? [Math.round(BASE_RESOLUTION * closestRatio), BASE_RESOLUTION]
      : [BASE_RESOLUTION, Math.round(BASE_RESOLUTION / closestRatio)]

  return {
    resolution: resolution.join('x'),
    ratioLabel: closestRatioLabel,
  }
}

/**
 * Fetches video metadata from Vimeo's oEmbed API including dimensions and thumbnail URL
 * @param url The Vimeo video URL
 * @returns Object containing the video's aspect ratio and thumbnail URL
 * // Returns:
 * // {
 * //   ratio: 1.7777777777777777, // 16/9 aspect ratio
 * //   thumbnailUrl: 'https://i.vimeocdn.com/video/2035897038-8ca0f3d96a92e7d88fd3ede2110190724f1df89517749ff3c9799d264f2234de-d_1920x1080'
 * // }
 */
async function fetchVideoInfo(url: string) {
  const vimeoApi = new URL('https://vimeo.com/api/oembed.json')
  vimeoApi.searchParams.set('url', url)

  const response = await fetch(vimeoApi)
  const { width, height, thumbnail_url } = (await response.json()) as Record<
    string,
    any
  >
  return {
    ratio: width / height,
    thumbnailUrl: thumbnail_url as string,
  }
}
